import type { WheelTourProgressStage } from "@/lib/wheelTourProgress";

export const WHEEL_TOUR_CREDIT_HANDOFF_KEY = "qw_tour_credit_eligible";

export type WheelTourCreditResult = {
  eligible: boolean;
  applied: boolean;
  pending: boolean;
  amount: number;
};

function normalizePath(value: string): string | null {
  try {
    const base = new URL("https://quantwheel.local");
    const parsed = new URL(value, base);
    if (parsed.origin !== base.origin) return null;
    const path = parsed.pathname.replace(/\/+$/, "");
    return path || "/";
  } catch {
    return null;
  }
}

export function isSameAppPath(href: string, expectedPath: string): boolean {
  const actual = normalizePath(href);
  const expected = normalizePath(expectedPath);
  return actual !== null && expected !== null && actual === expected;
}

export function markWheelTourCreditEligible(storage?: Pick<Storage, "setItem">): void {
  try {
    const target = storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
    target?.setItem(WHEEL_TOUR_CREDIT_HANDOFF_KEY, "1");
  } catch {
    // A blocked browser storage API must never stop tour completion.
  }
}

export async function issueWheelTourCreditProof(
  fetcher: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetcher("/api/onboarding/tour-completion", {
      method: "POST",
      credentials: "same-origin",
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function persistWheelTourProgress(
  input: { action: "start" } | { action: "stage"; stage: WheelTourProgressStage },
  fetcher: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetcher("/api/onboarding/tour-progress", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function createWheelTourProgressQueue(fetcher: typeof fetch = fetch) {
  let chain = Promise.resolve(true);
  return {
    start() {
      chain = persistWheelTourProgress({ action: "start" }, fetcher);
      return chain;
    },
    stage(stage: WheelTourProgressStage) {
      chain = chain.then((ready) => (
        ready ? persistWheelTourProgress({ action: "stage", stage }, fetcher) : false
      ));
      return chain;
    },
  };
}

export async function requestWheelTourCredit(
  fetcher: typeof fetch = fetch,
): Promise<WheelTourCreditResult | null> {
  try {
    const response = await fetcher("/api/onboarding/credits", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "tour" }),
    });
    if (!response.ok) return null;

    const result = await response.json() as Partial<WheelTourCreditResult>;
    if (
      typeof result.eligible !== "boolean"
      || typeof result.applied !== "boolean"
      || typeof result.pending !== "boolean"
      || typeof result.amount !== "number"
    ) {
      return null;
    }
    return result as WheelTourCreditResult;
  } catch {
    return null;
  }
}
